import type { BadgeRarity } from '../../types'

export interface RarityStyle {
  bg: string
  border: string
  text: string
  glow: string
  label: string
  desc: string
}

export const RARITY: Record<BadgeRarity, RarityStyle> = {
  common: {
    bg: '#f5f5f4',
    border: '#d6d3d1',
    text: '#57534e',
    glow: 'rgba(168,162,158,0.3)',
    label: 'Common',
    desc: 'Awarded to every adventurer who starts their journey.',
  },
  uncommon: {
    bg: '#dcfce7',
    border: '#86efac',
    text: '#15803d',
    glow: 'rgba(34,197,94,0.25)',
    label: 'Uncommon',
    desc: 'A solid step beyond the common — keep it up.',
  },
  rare: {
    bg: '#dbeafe',
    border: '#93c5fd',
    text: '#1d4ed8',
    glow: 'rgba(59,130,246,0.25)',
    label: 'Rare',
    desc: 'Only the dedicated few earn this distinction.',
  },
  epic: {
    bg: '#f3e8ff',
    border: '#c4b5fd',
    text: '#7e22ce',
    glow: 'rgba(139,92,246,0.3)',
    label: 'Epic',
    desc: 'A mark of exceptional skill and perseverance.',
  },
  legendary: {
    bg: 'linear-gradient(135deg,#fef9c3,#fde68a)',
    border: '#fcd34d',
    text: '#92400e',
    glow: 'rgba(217,119,6,0.4)',
    label: 'Legendary',
    desc: 'Reserved for the rarest of treasure hunters. True legends only.',
  },
}

export function rarityEmoji(rarity: BadgeRarity, category?: string): string {
  if (category) {
    const c = category.toLowerCase()
    if (c.includes('nature') || c.includes('park')) return '🌿'
    if (c.includes('explore') || c.includes('compass')) return '🧭'
    if (c.includes('veteran') || c.includes('master')) return '👑'
    if (c.includes('streak') || c.includes('fire')) return '🔥'
    if (c.includes('distance') || c.includes('walk')) return '🥾'
    if (c.includes('speed') || c.includes('quick')) return '⚡'
    if (c.includes('map') || c.includes('district')) return '🗺️'
    if (c.includes('grand') || c.includes('elite')) return '💎'
  }
  if (rarity === 'legendary') return '👑'
  if (rarity === 'epic') return '🔥'
  if (rarity === 'rare') return '🧭'
  if (rarity === 'uncommon') return '⚡'
  return '🌿'
}

export function getCriteriaDescription(criteria: Record<string, unknown>): string {
  const type = criteria?.type as string
  switch (type) {
    case 'hunt_complete':
      return 'Complete a specific treasure hunt.'
    case 'hunt_count':
      return `Complete ${criteria.count} treasure hunts.`
    case 'distance_traveled':
      return `Travel ${((criteria.meters as number) / 1000).toFixed(1)} km total.`
    case 'waypoint_count':
      return `Check in at ${criteria.count} waypoints.`
    case 'challenge_streak':
      return `Complete ${criteria.streak} challenges in a row.`
    case 'speed_run':
      return `Complete a hunt in under ${criteria.max_minutes} minutes.`
    default:
      return 'Complete the requirements to earn this badge.'
  }
}
